import { Injectable } from '@angular/core';

declare global {
  interface Window {
    gtag: (command: string, ...args: (string | number | boolean | Record<string, string | number | boolean>)[]) => void;
  }
}

export interface GA4Event {
  eventName: string;
  parameters?: Record<string, string | number | boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class GA4Service {
  private hasAnalyticsConsent = false;
  private hasAdsConsent = false;

  constructor() {
    this.checkConsent();
  }

  private checkConsent(): void {
    const stored = localStorage.getItem('cookie_consent');
    if (stored) {
      try {
        const prefs = JSON.parse(stored);
        this.hasAnalyticsConsent = prefs.analytics ?? false;
        this.hasAdsConsent = prefs.ads ?? false;
        this.applyConsent();
      } catch {
        // Invalid JSON, treat as no consent
        this.hasAnalyticsConsent = false;
        this.hasAdsConsent = false;
      }
    }
  }

  private applyConsent(): void {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': this.hasAnalyticsConsent ? 'granted' : 'denied',
        'ad_storage': this.hasAdsConsent ? 'granted' : 'denied'
      });
    }
  }

  updateConsent(hasAnalyticsConsent: boolean, hasAdsConsent: boolean = false): void {
    this.hasAnalyticsConsent = hasAnalyticsConsent;
    this.hasAdsConsent = hasAdsConsent;
    this.applyConsent();
  }

  trackEvent(eventName: string, parameters?: Record<string, string | number | boolean>): void {
    if (!this.hasAnalyticsConsent || typeof window.gtag === 'undefined') {
      return;
    }

    const event: GA4Event = {
      eventName,
      parameters: {
        ...parameters,
        timestamp: new Date().toISOString()
      }
    };

    window.gtag('event', eventName, event.parameters || {});
  }

  trackLoginStart(): void {
    this.trackEvent('login_start', {
      method: 'google'
    });
  }

  trackOAuthCallback(success: boolean): void {
    if (success) {
      this.trackEvent('oauth_success');
    } else {
      this.trackEvent('oauth_failure');
    }
  }

  trackGoogleAdsAccountSelection(accountId: string, accountName?: string): void {
    this.trackEvent('google_ads_account_selected', {
      account_id: accountId,
      account_name: accountName || 'Unknown'
    });
  }

  trackReportGeneration(accountType: string, hasConversionOverride: boolean, overrideConversionValue?: number): void {
    this.trackEvent('report_generated', {
      account_type: accountType,
      conversion_override: hasConversionOverride,
      override_conversion_value: overrideConversionValue || 'N/A'
    });
  }

  trackEnquiryFormSubmission(): void {
    this.trackEvent('enquiry_form_submitted', {
      form_type: 'contact'
    });
  }
}
